/* eslint-disable no-nested-ternary */
import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormatting } from 'locale';

import {
    Infobox, Loader, Title, Button,
    Icon,
} from 'ui-library';
import InvestmentAmountOptions from 'components/ShoppingCart/InvestmentAmountOptions/InvestmentAmountOptions';
import ExistingProductRow from 'components/ShoppingCart/ShoppingCartProductRow/ExistingProductRow';
import NewProductRow from 'components/ShoppingCart/ShoppingCartProductRow/NewProductRow';
import AddButton from 'components/common/AddButton';
import { ICON_TYPE_CLOSE_SLIM } from 'ui-library/components/Icon';
import { OverviewStepProps } from './ShoppingCard.types';
import './ShoppingCard.css';

const ShoppingCard: FC<OverviewStepProps> = ({ datasource }) => {
    const { t, i18n: { language } } = useTranslation();
    const { getAmountWithDecimals } = useFormatting();

    const {
        formData,
        advisoryId,
        desiredInvestmentAmount,
        existingPortfolios,
        remainingInvestmentAmount,
        fields,
        control,
        errors,
        handleDeleteRow,
        handleAddProduct,
        productOptions3B,
        saveNewProduct,
        newProductsLoading,
        cancelInProgress,
        handleConfig,
        is3aProductGroupSelector,
        is3bProductGroupSelector,
        isVbVbPremiumProductGroupSelector,
        is3aPremiumProductGroupSelector,
        selectedProductSelector,
        isAdvisoryProcessSelector,
        currentClientSelector,
        saveCurrentInvestmentApplicationIdAction,
        continueDisabled,
        advisoryDocumentId,
        advisoryDataLoading,
        isProofOfAssetsNeeded,
        hasPKApplication,
        saveInvestmentApplicationDataToStoreAction,
        removeInvestmentApplicationDataAction,
        optionPreferenceStock,
        optionPreferenceExclusion,
        optionPreferenceInclusion,
        canCheckInvestorProfile,
        handleCheckInvestorProfile,
        fakeLoading,
        checkInvestorProfileActive,
        cancelInvestorProfileCheck,
        isLoadingProducts,
    } = datasource();

    const addNewButtonDisabled = useMemo(() => {
        if ((isAdvisoryProcessSelector && remainingInvestmentAmount <= 0 && hasPKApplication) || (!is3bProductGroupSelector && fields?.length > 0) || (!selectedProductSelector && !is3bProductGroupSelector) || checkInvestorProfileActive || isLoadingProducts) {
            return true;
        }

        return false;
    }, [remainingInvestmentAmount, is3bProductGroupSelector, fields, selectedProductSelector, checkInvestorProfileActive, isLoadingProducts]);

    return (
        <div className="shopping-card new-portfolio agent-info">
            <Title type={2} className="mb-16">
                {t('ShoppingCart.portfolioPersonalOverview')}
            </Title>
            {advisoryDataLoading ? <Loader /> : isAdvisoryProcessSelector ? (
                <InvestmentAmountOptions
                    desiredInvestmentAmount={desiredInvestmentAmount}
                    remainingInvestmentAmount={remainingInvestmentAmount}
                />
            ) : null}

            <div className="products-on-cart">
                <Title type={4}>
                    {t('ShoppingCart.newProductsTitle')}
                </Title>
                {newProductsLoading ? <Loader /> : fields?.length ? fields?.map((item, index) => (
                    <NewProductRow
                        control={control}
                        errors={errors}
                        key={item?.investmentApplicationId || index}
                        index={index}
                        product={item}
                        configured={item?.isConfigured}
                        handleDeleteRow={handleDeleteRow}
                        productOptions={productOptions3B}
                        saveNewProduct={saveNewProduct}
                        formData={formData?.products?.[index]}
                        cancelInProgress={cancelInProgress}
                        handleConfig={handleConfig}
                        selectedProductSelector={selectedProductSelector}
                        is3aProductGroupSelector={is3aProductGroupSelector}
                        is3bProductGroupSelector={is3bProductGroupSelector}
                        isVbVbPremiumProductGroupSelector={isVbVbPremiumProductGroupSelector}
                        is3aPremiumProductGroupSelector={is3aPremiumProductGroupSelector}
                        currentClientSelector={currentClientSelector}
                        saveCurrentInvestmentApplicationIdAction={saveCurrentInvestmentApplicationIdAction}
                        continueDisabled={continueDisabled}
                        t={t}
                        language={language}
                        getAmountWithDecimals={getAmountWithDecimals}
                    />
                )) : null}
                <AddButton
                    onClick={handleAddProduct}
                    label={
                        t('ShoppingCart.addNewProductButton.label')
                    }
                    className="mb-32"
                    disabled={addNewButtonDisabled}
                    loading={isLoadingProducts}
                />
            </div>

            {existingPortfolios?.loading ? <Loader /> : (isAdvisoryProcessSelector && existingPortfolios?.data?.length > 0)
                ? (
                    <div className="products-on-cart mt-30">
                        <Title type={4}>
                            {t('ShoppingCart.existing3bProductsTitle')}
                        </Title>

                        {existingPortfolios?.data?.map((product, index) => (
                            <ExistingProductRow
                                key={product?.id || index}
                                product={product}
                                advisoryDocumentId={advisoryDocumentId}
                                remainingInvestmentAmount={remainingInvestmentAmount}
                                t={t}
                                language={language}
                                getAmountWithDecimals={getAmountWithDecimals}
                                saveInvestmentApplicationDataToStoreAction={saveInvestmentApplicationDataToStoreAction}
                                removeInvestmentApplicationDataAction={removeInvestmentApplicationDataAction}
                                optionPreferenceStock={optionPreferenceStock}
                                optionPreferenceExclusion={optionPreferenceExclusion}
                                optionPreferenceInclusion={optionPreferenceInclusion}
                                advisoryId={advisoryId}
                                canCheckInvestorProfile={canCheckInvestorProfile}
                                checkInvestorProfileActive={checkInvestorProfileActive}
                                handleCheckInvestorProfile={handleCheckInvestorProfile}
                                fakeLoading={fakeLoading}
                            />
                        ))}

                        {checkInvestorProfileActive && (
                            <Infobox className="check-investor-profile-active-info">
                                <div className="flex align-start justify-content-between gap-20">
                                    {t('ShoppingCart.Modal.CheckInvestorProfile.ActiveInfo')}
                                    <Button disabled={fakeLoading} loading={fakeLoading} className="ml-20" type="secondary" onClick={cancelInvestorProfileCheck}>
                                        <Icon size={24} type={ICON_TYPE_CLOSE_SLIM} className="mr-8" />
                                        {t('CheckInvestorProfile.button')}
                                    </Button>
                                </div>
                            </Infobox>
                        )}
                    </div>
                ) : null}

            {(isProofOfAssetsNeeded) ? (
                <Infobox warning showIcon={false} className="mb-32">
                    {t('3b.performance.proofOfAssetsWarning')}
                </Infobox>
            ) : null}
        </div>
    );
};

export default ShoppingCard;
